import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.jpg';
import { getActiveEnvMode } from '../lib/supabase';

// Prod is the canonical source of accounts: test.leafandletter.site is a
// beta/QA environment whose accounts get periodically mirrored from prod
// (see sync_accounts_prod_to_test.sh on the VPS), not the other way
// around. An account created directly on test never exists on prod and
// risks getting confused/orphaned by that sync -- so signup is hidden
// here on test, with a banner pointing people to register on prod.
const isTestEnv = getActiveEnvMode() === 'test';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const { signIn, signUp, signInWithSSO } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (isSignUp && isTestEnv) {
      setError('Sign-ups are disabled on the test/beta site. Please create your account on leafandletter.site — test accounts are mirrored from prod automatically.');
      return;
    }

    if (isSignUp) {
      const { error } = await signUp(email, password);
      if (error) {
        setError(error.message);
      } else {
        setMessage('Welcome! Check your email for a confirmation link before signing in.');
      }
    } else {
      const { error } = await signIn(email, password);
      if (error) {
        setError('Incorrect email or password.');
      } else {
        navigate('/');
      }
    }
  };

  return (
    <div
      style={{
        maxWidth: '400px',
        margin: '100px auto',
        padding: '32px 24px',
        backgroundColor: 'var(--bg-card)',
        borderRadius: '16px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
        <img
          src={logo}
          alt="Leaf & Letter"
          style={{ height: '80px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
        />
      </div>

      {isTestEnv && (
        <div
          style={{
            marginBottom: '20px',
            padding: '10px 12px',
            borderRadius: '8px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #bbdefb',
            color: '#1565c0',
            fontSize: '0.78rem',
            lineHeight: 1.4,
            textAlign: 'center',
          }}
        >
          This is the <strong>test/beta</strong> environment. Accounts are created on{' '}
          <strong>leafandletter.site</strong> (prod) and mirrored here — sign in with your prod
          credentials rather than creating a new account.
        </div>
      )}

      <h2 style={{ textAlign: 'center', color: 'var(--text-main)', marginBottom: '8px' }}>
        {isSignUp ? 'Join the Sanctuary' : 'Welcome Back'}
      </h2>
      <p style={{ textAlign: 'center', color: 'var(--text-soft)', fontSize: '0.85rem', marginBottom: '24px' }}>
        {isSignUp ? 'Create your reading sanctuary.' : 'Sign in to your library.'}
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', color: 'var(--text-soft)', fontSize: '0.9rem' }}>
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-app)',
              color: 'var(--text-main)',
              fontSize: '0.95rem',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '6px', color: 'var(--text-soft)', fontSize: '0.9rem' }}>
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-app)',
              color: 'var(--text-main)',
              fontSize: '0.95rem',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {error && (
          <p style={{ color: '#e05252', fontSize: '0.85rem', margin: 0 }}>{error}</p>
        )}
        {message && (
          <p style={{ color: 'var(--accent)', fontSize: '0.85rem', margin: 0 }}>{message}</p>
        )}

        <button
          type="submit"
          style={{
            padding: '12px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: 'var(--accent)',
            color: 'white',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '1rem',
            marginTop: '4px',
            boxShadow: '0 4px 12px var(--accent-soft)',
          }}
        >
          {isSignUp ? 'Create Account' : 'Sign In'}
        </button>
      </form>

      {!(isTestEnv && !isSignUp) && (
        <p style={{ textAlign: 'center', marginTop: '24px', color: 'var(--text-soft)', fontSize: '0.85rem', marginBottom: '12px' }}>
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}
          <button
            onClick={() => { setIsSignUp(!isSignUp); setError(null); setMessage(null); }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent)',
              cursor: 'pointer',
              fontWeight: 'bold',
              marginLeft: '6px',
              textDecoration: 'underline',
              fontSize: '0.85rem',
            }}
          >
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </button>
        </p>
      )}

      {!isSignUp && (
        <p style={{ textAlign: 'center', marginTop: '16px', marginBottom: 0 }}>
          <button
            onClick={signInWithSSO}
            type="button"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-soft)',
              cursor: 'pointer',
              fontSize: '0.72rem',
              opacity: 0.5,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => e.target.style.opacity = 0.9}
            onMouseLeave={(e) => e.target.style.opacity = 0.5}
          >
            Employee Sign In
          </button>
        </p>
      )}
    </div>
  );
};

export default Auth;
